import { Navigate, useLocation } from 'react-router-dom';
import LoadingScreen from '~/components/LoadingScreen';
import {
  useHasCashflow,
  useHasHideBalances,
  useHasOutstanding,
  useHasSeeInvoices,
  useHasTreasury,
  useIsBankAccountLeadSource,
  useIsPayrollConsultant,
} from '~/hooks/useFeatureFlag';
import useGettingStartedTasksCount from '~/hooks/useGettingStartedTasksCount';
import { useUserBankAccountPermissions } from '~/hooks/useUserBankAccountPermissions';
import { useVisibleBankingConsents } from '~/hooks/useVisibleBankingConsents';
import RedirectWithQueryParams from '~/routes/RedirectWithQueryParams';
import { RouteConfig } from '~/routes/routeConfig';
import { useLoggedUser } from '~/services/user';
import { isConsentAuthorised, isSwanConsent } from '~/utils/consents';

const DashboardRedirects: React.FunctionComponent = () => {
  const userQuery = useLoggedUser();
  const hasTreasury = useHasTreasury();
  const hasCashflow = useHasCashflow();
  const hasHideBalances = useHasHideBalances();
  const hasSeeInvoices = useHasSeeInvoices();
  const hasOustanding = useHasOutstanding();
  const isBankAccountLeadSource = useIsBankAccountLeadSource();
  const isPayrollConsultant = useIsPayrollConsultant();
  const { pathname } = useLocation();
  const { visibleConsents, consentsQuery } = useVisibleBankingConsents();
  const {
    canInitiatePayments,
    canViewAccount,
    isSibillAccountEnabled,
    isLoading: bankAccountLoading,
  } = useUserBankAccountPermissions();

  const { isLoading: gettingStartedLoading } = useGettingStartedTasksCount();
  const loading = gettingStartedLoading || userQuery.isLoading || consentsQuery.isLoading || bankAccountLoading;

  if (!userQuery.isLoading && !userQuery.data?.companies.length && pathname.startsWith('/signup') === false) {
    return <RedirectWithQueryParams path={RouteConfig.SignUpAddCompany.buildLink()} />;
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (isPayrollConsultant) {
    return <Navigate to={RouteConfig.GeneralBookkeepingNonElectronicDocuments.buildLink()} />;
  }

  if (hasCashflow && !hasHideBalances) {
    return <Navigate to={RouteConfig.Cashflow.buildLink()} />;
  }

  if (isBankAccountLeadSource) {
    const hasNonAuthorizedSwanConsent = visibleConsents.some(
      (consent) => isSwanConsent(consent) && !isConsentAuthorised(consent),
    );

    if (hasNonAuthorizedSwanConsent) {
      return <Navigate to={RouteConfig.Accounts.buildLink()} />;
    }
  }

  if (hasTreasury) {
    return <Navigate to={RouteConfig.Movements.buildLink()} />;
  }

  if (hasSeeInvoices) {
    return <Navigate to={RouteConfig.Invoices.buildLink()} />;
  }

  if (hasOustanding) {
    return <Navigate to={RouteConfig.Outstanding.buildLink()} />;
  }

  if (canInitiatePayments) {
    return <Navigate to={RouteConfig.Payments.buildLink()} />;
  }

  if (canViewAccount) {
    return <Navigate to={RouteConfig.Accounts.buildLink()} />;
  }

  if (isSibillAccountEnabled) {
    return <Navigate to={RouteConfig.AccountsCards.buildLink()} />;
  }

  return <Navigate to={RouteConfig.UnauthorisedCompany.buildLink()} />;
};

export default DashboardRedirects;
